/**
 * Shared value vocabulary for event-ticketing GA4 params. Event names + param keys live in
 * `ga-events.ts` (GA4EventName / GA4EventParamName); this file holds only the string *values*
 * so they are defined once across the wizard, dashboard, and buyer-checkout surfaces.
 * Mapping from domain enums (RegistrationMode, QuestionBelongsTo, TaxMode, scan status) to these
 * values lives next to each call site to keep this file free of component imports.
 */

export enum TicketingRegistrationTypeParam {
  TICKETS = 'tickets',
  RSVP = 'rsvp',
  CUSTOM_LINK = 'custom_link',
  NONE = 'none',
}

/** Events_Ticketing_Setup_Complete `type` and Events_Ticketing_Checkout_Start `type`. */
export enum TicketingPaidFreeParam {
  PAID = 'paid',
  FREE = 'free',
}

export enum TicketingFeeStrategyParam {
  ABSORB = 'absorb',
  PASS = 'pass',
}

export enum TicketingFormScopeParam {
  BUYER = 'buyer',
  ALL_ATTENDEES = 'all_attendees',
}

export enum TicketingTaxParam {
  NONE = 'none',
  INCLUSIVE = 'inclusive',
  EXCLUSIVE = 'exclusive',
}

/** Events_Create_Complete `event_type` — the paid/free split (TicketingPaidFreeParam) plus the non-tickets
 * registration modes (TicketingRegistrationTypeParam minus TICKETS). Composed from the existing enums so the
 * shared values ('rsvp'/'custom_link'/'none', 'paid'/'free') are never re-declared. */
export type TicketingEventTypeParam = TicketingPaidFreeParam | Exclude<TicketingRegistrationTypeParam, TicketingRegistrationTypeParam.TICKETS>;

/** Events_Ticketing_Checkin_Scan `result` — mirrors TicketingScanResultStatus (checked_in→valid, already_checked_in→already_used). */
export enum TicketingScanResultParam {
  VALID = 'valid',
  ALREADY_USED = 'already_used',
  REFUNDED = 'refunded',
  CANCELLED = 'cancelled',
  NOT_FOUND = 'not_found',
  ON_HOLD = 'on_hold',
}

/** Events_Ticketing_Checkin_Mode_Enter `mode` — which check-in surface the organiser opened. */
export enum TicketingCheckinModeParam {
  SCANNER = 'scanner',
  MANUAL = 'manual',
}

/** Events_Ticketing_Checkin_Manual `scope` — a whole order vs a single attendee. */
export enum TicketingCheckinScopeParam {
  ORDER = 'order',
  ATTENDEE = 'attendee',
}

export enum TicketingTicketActionParam {
  RESEND = 'resend',
  CANCEL = 'cancel',
  MARK_PAID = 'mark_paid',
}

export enum TicketingRefundSourceParam {
  ORGANIZER = 'organizer',
  BUYER = 'buyer',
}

/** Events_Ticketing_Attendee_Add `email_opt_in` / `checked_in`. */
export enum TicketingYesNoParam {
  YES = 'yes',
  NO = 'no',
}

/** Events_Ticketing_Upgrade_Click `source`. */
export enum TicketingUpgradeSourceParam {
  SERVICE_FEE = 'service_fee',
}

/** Reason the paid-ticketing coming-soon (fake-door) modal opened — added to Events_TicketingComingSoon. */
export enum TicketingComingSoonReasonParam {
  NON_US = 'ticketing_non_us',
  FEATURE = 'ticketing_feature',
}
